import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronDown, X } from 'lucide-react';
import { SequenceFormData } from './types';
import { enhancedSpotifyService } from '@/services/enhancedSpotifyService';

interface Option {
  id: string;
  name: string;
}

interface GenresMultiSelectProps {
  form: UseFormReturn<SequenceFormData>;
  max?: number;
}

const GenresMultiSelect: React.FC<GenresMultiSelectProps> = ({ form, max = 6 }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [query, setQuery] = useState('');

  const selected = (form.watch('genres') || []) as string[];

  useEffect(() => {
    const load = async () => {
      // popular genres first
      const popular = await enhancedSpotifyService.getPopularGenres(25);
      setOptions(popular.map(g => ({ id: g.id, name: g.name })));
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const addGenre = (name: string) => {
    const current = (form.getValues('genres') || []) as string[];
    if (current.includes(name)) return;
    if (current.length >= max) return;
    const next = [...current, name];
    form.setValue('genres', next, { shouldDirty: true, shouldValidate: true });
    // Keep primary/secondary aligned with first two entries
    if (!form.getValues('primary_genre') && next[0]) form.setValue('primary_genre', next[0]);
    if (next.length >= 2) form.setValue('secondary_genre', next[1]);
  };

  const removeGenre = (name: string) => {
    const current = (form.getValues('genres') || []) as string[];
    const next = current.filter(g => g !== name);
    form.setValue('genres', next, { shouldDirty: true, shouldValidate: true });
    // Re-sync primary/secondary
    const p = next[0];
    const s = next[1];
    form.setValue('primary_genre', p);
    form.setValue('secondary_genre', s);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {selected.map(name => (
          <Badge key={name} variant="secondary" className="flex items-center gap-1">
            {name}
            <button type="button" onClick={() => removeGenre(name)} aria-label={`Remove ${name}`}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="justify-between w-full">
            Add genre{selected.length > 0 ? ` (${selected.length}/${max})` : ''}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[320px]">
          <Command>
            <CommandInput placeholder="Search genres..." value={query} onValueChange={setQuery} />
            <CommandEmpty>
              <div className="p-3">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Type a new genre"
                />
                <Button type="button" className="mt-2 w-full" onClick={() => { addGenre(query.trim()); setQuery(''); }} disabled={!query.trim()}>
                  Add "{query.trim()}"
                </Button>
              </div>
            </CommandEmpty>
            <CommandList>
              <CommandGroup>
                {filtered.map(opt => (
                  <CommandItem key={opt.id} value={opt.name} onSelect={() => { addGenre(opt.name); setQuery(''); }}>
                    {opt.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default GenresMultiSelect;


